'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Pill, Sparkles, Utensils, GlassWater, Settings, FlaskConical, Barcode, ShieldCheck, Tag } from 'lucide-react';

const industries = [
  { name: 'FMCG Labels', icon: ShoppingBag, desc: 'High-speed labeling for personal care, household, and consumer goods.' },
  { name: 'Pharmaceutical Labels', icon: Pill, desc: 'FDA-compliant, highly legible labels with zero-migration printing.' },
  { name: 'Cosmetic Labels', icon: Sparkles, desc: 'Premium, tactile, and moisture-resistant finishes for luxury aesthetics.' },
  { name: 'Food Packaging Labels', icon: Utensils, desc: 'Food-safe substrates and low-migration inks for clean branding.' },
  { name: 'Beverage Labels', icon: GlassWater, desc: 'Water-proof, ice-bucket tested labels with stunning foiling effects.' },
  { name: 'Industrial Product Labels', icon: Settings, desc: 'Highly durable labels resistant to abrasion, heat, and chemicals.' },
  { name: 'Chemical Labels', icon: FlaskConical, desc: 'GHS-compliant, weather-proof labels designed for chemical packaging.' },
  { name: 'Logistics & Barcode Labels', icon: Barcode, desc: 'High-density barcode and QR code printing for seamless tracing.' },
  { name: 'Security & Tamper Evident Labels', icon: ShieldCheck, desc: 'Taggants, covert inks, and void patterns for authentication.' },
  { name: 'Retail Packaging Labels', icon: Tag, desc: 'Vibrant, high-impact retail shelf labeling to capture consumer attention.' }
];

const AboutIndustries = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <section className="w-full bg-[#F8FAFC] py-16 md:py-24" aria-labelledby="industries-heading">
      <div className="mx-auto w-full max-w-[1240px] px-4 sm:px-6 md:px-8">
        <header className="mb-12 md:mb-16 text-center max-w-[800px] mx-auto">
          <span className="text-blue-600 font-bold tracking-[3px] uppercase text-xs mb-3 block">Specialized Expertise</span>
          <h2
            id="industries-heading"
            className="text-[#0F172A] font-extrabold tracking-tight text-[clamp(28px,4vw,38px)] leading-[1.2]"
          >
            Industries We Serve
          </h2>
          <p className="mt-4 text-slate-600 text-[16px] md:text-[17px] leading-relaxed">
            Delivering high-performance, industry-specific labeling solutions that meet strict compliance and branding requirements across multiple B2B sectors.
          </p>
        </header>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6"
        >
          {industries.map((ind, index) => (
            <motion.article
              key={index}
              variants={itemVariants}
              className="bg-white p-6 rounded-2xl border border-slate-200 shadow-[0_4px_12px_rgba(0,0,0,0.02)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300 group flex flex-col items-start"
            >
              <div 
                className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300 text-[#C9A84C] group-hover:text-white"
                style={{
                  background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
                  boxShadow: '0 4px 12px rgba(201,168,76,0.12)',
                  border: '1px solid rgba(201,168,76,0.3)',
                }}
              >
                <ind.icon className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <h3 className="text-slate-900 font-bold text-[16px] leading-[1.3] group-hover:text-blue-600 transition-colors duration-300">
                {ind.name}
              </h3>
              <p className="mt-2 text-slate-500 text-[13px] leading-relaxed">
                {ind.desc}
              </p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutIndustries;
